import React, { useEffect, useState } from "react";
import "../Styling/balancesheet.css";
import axios from "axios";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';

const ProfitLossReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  
  // Filter states
  const [filters, setFilters] = useState({
    organisationId: user?.organisationId || "",
    companyCode: "",
    fiscalYear: new Date().getFullYear(),
    fiscalMonth: "",
  });
  
  const [organisations, setOrganisations] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);

  
  useEffect(() => {
const fetchOrganisations = async () => {
  try {
    const response = await axios.get(
      `${Base_URL}/Balancesheet/organisations-with-names`,
      { headers: { Authorization: `Bearer ${user?.token}` } }
    );
    setOrganisations(response.data);
  } catch (error) {
    console.error("Error fetching organisations:", error);
  }
};
;
    if (user?.token) fetchOrganisations();
  }, [user]);

  useEffect(() => {
    const fetchCompanyCodes = async () => {
  if (!filters.organisationId) return;
  
  try {
    const response = await axios.get(
      `${Base_URL}/Balancesheet/company-codes-with-names/${user?.organisationId}`,  // Updated endpoint
      { headers: { Authorization: `Bearer ${user?.token}` } }
    );
    setCompanyCodes(response.data);
  } catch (error) {
    console.error("Error fetching company codes:", error);
  }
};

    if (user?.organisationId) fetchCompanyCodes();
  }, [user?.organisationId, user]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 5; i >= 0; i--) {
      years.push(currentYear - i);
    }
    setFiscalYears(years);
  }, []);

  // Fetch profit loss data when filters change
  useEffect(() => {
    const fetchProfitLossData = async () => {
      if (!filters?.organisationId || !filters.companyCode) {
        setData(null);
        return;
      }

      try {
        setLoading(true);
        
        
        let url = `${Base_URL}/Balancesheet/profit-loss/${filters?.organisationId}/${filters.companyCode}/${filters.fiscalYear}`;
        
        if (filters.fiscalMonth) {
          url += `/${filters.fiscalMonth}`;
        }

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        });
        setData(response.data);
      } catch (error) {
        console.error("Error fetching profit & loss:", error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    // Small delay to prevent too many API calls
    const timer = setTimeout(() => {
      fetchProfitLossData();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters, user]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      organisationId: user?.organisationId || "",
      companyCode: "",
      fiscalYear: new Date().getFullYear(),
      fiscalMonth: "",
    });
    setData(null);
  };

const generatePDF = () => {
    if (!data) return;

  const formatAmountForPDF = (amount) => {
  const num = Number(amount || 0);
  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10; // Standard margin for consistency
    let currentY = 15; // Start position for the first element

    // Company Name (Green)
    doc.setFontSize(18);
    doc.setTextColor(40, 167, 69); // Green
    doc.text(data.companyName, pageWidth / 2, currentY, { align: "center" });
    currentY += 7;

    // Company Address (Grey)
    doc.setFontSize(10);
    doc.setTextColor(100); // Grey
    doc.text(data.companyAddress, pageWidth / 2, currentY, { align: "center" });
    currentY += 10;

    // Dynamic Report Title (Green)
    doc.setFontSize(15);
    doc.setTextColor(40, 167, 69); // Green
    let reportTitle = `Profit & Loss Statement - FY ${filters.fiscalYear}`;
    if (filters.fiscalMonth) {
      const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"];
      reportTitle = `Profit & Loss Statement - ${monthNames[parseInt(filters.fiscalMonth) - 1]} ${filters.fiscalYear}`;
    }
    doc.text(reportTitle, pageWidth / 2, currentY, { align: "center" });
    currentY += 7;

    // Company Code & Filter Info (Small Grey)
    doc.setFontSize(8);
    doc.setTextColor(100); // Grey
    
    // Company Code
    doc.text(`Company Code: ${data.companyCode || filters.companyCode}`, pageWidth / 2, currentY, { align: "center" });
    currentY += 5;
    
    // Filter Info
    let filterInfo = `Organisation: ${organisations.find(o => o.organisationId == filters.organisationId)?.organisationName || filters.organisationId}`;
    filterInfo += ` | FY: ${filters.fiscalYear}`;
    if (filters.fiscalMonth) {
      filterInfo += ` | Month: ${filters.fiscalMonth}`;
    }
    doc.text(filterInfo, pageWidth / 2, currentY, { align: "center" });
    currentY += 10; // Extra space before the first section

    // --- 2. Income Section ---
    
    // Income Heading (Green)
    doc.setFontSize(13);
    doc.setTextColor(40, 167, 69); // Green
    doc.text("Income", margin, currentY);
    currentY += 2; // Space before table

    // Income Table
    autoTable(doc, {
      startY: currentY,
      // FIX: Use plain, clean string for header
      head: [["Income Categories", "Amount (₹)"]], 
      body: data.incomeSummary?.categories?.map(item => [
        formatCategoryName(item.category),
        // FIX: Use the safe formatting function
        formatAmountForPDF(item.amount) 
      ]) || [],
      headStyles: { fillColor: [40, 167, 69], textColor: [255, 255, 255] }, 
      margin: { left: margin, right: margin },
      styles: { fontSize: 10, cellPadding: 2 },
      columnStyles: {
        1: { halign: 'right' }
      }
    });
    
    // Crucial step: Update Y position based on the table's final position
    currentY = doc.lastAutoTable.finalY + 5;

    // Total Income (Black)
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0); // Black for main totals

    const totalIncomeText = "Total Income:";
    
   const totalIncomeAmount = `INR ${formatAmountForPDF(data.incomeSummary?.total)}`;


    doc.text(totalIncomeText, 150, currentY, { align: 'right' }); 
    doc.text(totalIncomeAmount, pageWidth - margin, currentY, { align: 'right' });
    currentY += 10; // Space before next section

    // --- 3. Expense Section ---

    // Expense Heading (Red)
    doc.setFontSize(13);
    doc.setTextColor(220, 53, 69); // Red
    doc.text("Expenses", margin, currentY);
    currentY += 2; // Space before table

    // Expense Table
    autoTable(doc, {
      startY: currentY,
      // FIX: Use plain, clean string for header
      head: [["Expense Categories", "Amount (₹)"]],
      body: data.expenseSummary?.categories?.map(item => [
        formatCategoryName(item.category),
        // FIX: Use the safe formatting function
        formatAmountForPDF(item.amount) 
      ]) || [],
      headStyles: { fillColor: [220, 53, 69], textColor: [255, 255, 255] },
      margin: { left: margin, right: margin },
      styles: { fontSize: 10, cellPadding: 2 },
      columnStyles: {
        1: { halign: 'right' }
      }
    });

    // Crucial step: Update Y position based on the table's final position
    currentY = doc.lastAutoTable.finalY + 5;

    // Total Expense (Black)
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0); // Black for main totals

    const totalExpenseText = "Total Expenses:";
  const totalExpenseAmount = `INR ${formatAmountForPDF(data.expenseSummary?.total)}`;

    
    doc.text(totalExpenseText, 150, currentY, { align: 'right' });
    doc.text(totalExpenseAmount, pageWidth - margin, currentY, { align: 'right' });
    currentY += 10; // Space before Net P/L

    // --- 4. Net Profit/Loss ---

    // Horizontal Rule/Separator
    doc.setDrawColor(100); // Grey line
    doc.setLineWidth(0.5);
    doc.line(margin, currentY, pageWidth - margin, currentY); 
    currentY += 5;

    // Net P/L Heading (Conditional Color)
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold"); // Make it bold
    
    if (data.isProfit) {
        doc.setTextColor(40, 167, 69); // Green for Profit
    } else {
        doc.setTextColor(220, 53, 69); // Red for Loss
    }

    const netResultText = `Net ${data.isProfit ? "Profit" : "Loss"}:`;
    // FIX: Prepend the plain '₹' symbol to the safely formatted number
 const netResultAmount = `INR ${formatAmountForPDF(data.netProfitLoss)}`;


    doc.text(netResultText, 150, currentY, { align: 'right' });
    doc.text(netResultAmount, pageWidth - margin, currentY, { align: 'right' });
    currentY += 5; 

    // Reset font style
    doc.setFont("helvetica", "normal");
    
    // --- 5. Footer ---

    const footerY = 280; // Fixed position near the bottom

    doc.setFontSize(9);
    doc.setTextColor(100); // Grey

    // Left Footer
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, footerY);
    
    // Right Footer
    doc.text(`Report Period: ${data.reportPeriod || "Yearly"}`, pageWidth - margin, footerY, { align: 'right' });

    // Save the PDF
    doc.save(`profit-loss-${filters.companyCode}-${filters.fiscalYear}${filters.fiscalMonth ? '-month-' + filters.fiscalMonth : ''}.pdf`);
};
  const monthOptions = [
    { value: "", label: "All Months (Yearly)" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const formatCategoryName = (name) => {
    return name.replace(/([A-Z])/g, " $1").trim();
  };

  const getReportPeriodText = () => {
    if (filters.fiscalMonth) {
      const monthName = monthOptions.find(m => m.value === filters.fiscalMonth)?.label;
      return `For the month of ${monthName}, ${filters.fiscalYear}`;
    }
    return `For the year ended December 31, ${filters.fiscalYear}`;
  };

  // Get selected company name
  const getSelectedCompanyName = () => {
    if (!filters.companyCode) return "";
    const company = companyCodes.find(c => c.companyCode == filters.companyCode);
    return company ? `${company.companyCode} - ${company.companyName}` : filters.companyCode;
  };

  // Get selected organisation name
  const getSelectedOrganisationName = () => {
    if (!filters.organisationId) return "";
    const org = organisations.find(o => o.organisationId == filters.organisationId);
    return org ? org.organisationName : filters.organisationId;
  };

  return (
    <div className="plr-wrapper">
      {/* Filter Section */}
      <div className="plr-filters-container">
        <div className="plr-filters-header">
          <h3>Profit & Loss Statement Filters</h3>
          <div className="plr-filter-actions">
            <button onClick={handleResetFilters} className="plr-reset-btn">
              Reset Filters
            </button>
          </div>
        </div>
        
        <div className="plr-filters-grid">
          <div className="plr-filter-group">
            <label htmlFor="organisationId">Organisation </label>
            <select
              id="organisationId"
              name="organisationId"
              value={filters.organisationId}
              onChange={handleFilterChange}
              className="plr-filter-select"
              required
            >
              <option value="">Select Organisation</option>
              {organisations.map((org) => (
                <option key={org.organisationId} value={org.organisationId}>
                  {org.organisationName} ({org.organisationId})
                </option>
              ))}
            </select>
          </div>

          <div className="plr-filter-group">
            <label htmlFor="companyCode">Company Code</label>
            <select
              id="companyCode"
              name="companyCode"
              value={filters.companyCode}
              onChange={handleFilterChange}
              className="plr-filter-select"
              required
              disabled={!filters.organisationId || companyCodes.length === 0}
            >
              <option value="">Select Company Code</option>
              {companyCodes.map((company) => (
                <option key={company.companyCode} value={company.companyCode}>
                  {company.companyCode} - {company.companyName}
                </option>
              ))}
            </select>
            {filters.organisationId && companyCodes.length === 0 && (
              <small className="plr-filter-warning">No company codes found for this organisation</small>
            )}
          </div>

          <div className="plr-filter-group">
            <label htmlFor="fiscalYear">Fiscal Year *</label>
            <select
              id="fiscalYear"
              name="fiscalYear"
              value={filters.fiscalYear}
              onChange={handleFilterChange}
              className="plr-filter-select"
              required
            >
              {fiscalYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="plr-filter-group">
            <label htmlFor="fiscalMonth">Fiscal Month</label>
            <select
              id="fiscalMonth"
              name="fiscalMonth"
              value={filters.fiscalMonth}
              onChange={handleFilterChange}
              className="plr-filter-select"
            >
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="plr-active-filters">
          <small>
            <strong>Active Filters:</strong>
            {filters.organisationId && ` Organisation: ${getSelectedOrganisationName()}`}
            {filters.companyCode && ` | Company: ${getSelectedCompanyName()}`}
            {` | Fiscal Year: ${filters.fiscalYear}`}
            {filters.fiscalMonth && ` | Month: ${monthOptions.find(m => m.value === filters.fiscalMonth)?.label}`}
            {loading && " | Loading..."}
          </small>
        </div>
      </div>

      {/* Report Header with PDF button */}
      {data && (
        <div className="plr-header">
          <div className="plr-pdf-button">
            <button 
              onClick={generatePDF} 
              className="plr-pdf-btn"
              disabled={loading}
            >
              <i className="fas fa-file-pdf"></i> Download PDF
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="plr-loading">
          <div className="plr-loading-spinner"></div>
          <p>Loading Profit & Loss Statement...</p>
        </div>
      )}

      {/* Main Content */}
      {!loading && data && (
        <div className="plr-container">
          {/* Company Info */}
          <div className="plr-company-header">
            {data.logoUrl && data.logoUrl !== "No Logo Available" && (
              <img src={data.logoUrl} alt="Logo" className="plr-logo" />
            )}
            <div className="plr-company-text">
              <h2>{data.companyName}</h2>
              <p className="plr-company-address">{data.companyAddress}</p>
              <div className="plr-company-meta">
                <span className="plr-company-code">Company Code: {data.companyCode || filters.companyCode}</span>
              </div>
            </div>
          </div>

          {/* Report Title */}
          <div className="plr-report-title">
            <h1>Profit & Loss Statement</h1>
            <p className="plr-year">{getReportPeriodText()}</p>
            <p className="plr-report-period">{data.reportPeriod || "Yearly Report"}</p>
          </div>

          {/* Summary Cards */}
          <div className="plr-summary-cards">
            <div className="plr-summary-card plr-income-card">
              <div className="plr-summary-label">Total Income</div>
              <div className="plr-summary-amount plr-income-amount">
                ₹{(data.incomeSummary?.total || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </div>
            </div>
            
            <div className="plr-summary-card plr-expense-card">
              <div className="plr-summary-label">Total Expenses</div>
              <div className="plr-summary-amount plr-expense-amount">
                ₹{(data.expenseSummary?.total || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </div>
            </div>
            
            <div className={`plr-summary-card ${data.isProfit ? 'plr-profit-card' : 'plr-loss-card'}`}>
              <div className="plr-summary-label">Net {data.isProfit ? "Profit" : "Loss"}</div>
              <div className={`plr-summary-amount ${data.isProfit ? 'plr-profit-amount' : 'plr-loss-amount'}`}>
                ₹{Math.abs(data.netProfitLoss || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </div>
            </div>
          </div>

          {/* Income Section */}
          <div className="plr-section">
            <h3 className="plr-section-title plr-income-title">
              Income
              <span className="plr-section-total">
                Total: ₹{(data.incomeSummary?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </h3>
            <table className="plr-table plr-income-table">
              <thead>
                <tr>
                  <th width="70%">Particulars</th>
                  <th width="30%">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {data.incomeSummary?.categories?.map((item) => (
                  <tr key={item.category} className="plr-income-row">
                    <td>{formatCategoryName(item.category)}</td>
                    <td className="plr-amount">
                      {item.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
                {(!data.incomeSummary?.categories || data.incomeSummary.categories.length === 0) && (
                  <tr>
                    <td colSpan="2" className="plr-no-data-row">No income data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Expense Section */}
          <div className="plr-section">
            <h3 className="plr-section-title plr-expense-title">
              Expenses
              <span className="plr-section-total">
                Total: ₹{(data.expenseSummary?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </h3>
            <table className="plr-table plr-expense-table">
              <thead>
                <tr>
                  <th width="70%">Particulars</th>
                  <th width="30%">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {data.expenseSummary?.categories?.map((item) => (
                  <tr key={item.category} className="plr-expense-row">
                    <td>{formatCategoryName(item.category)}</td>
                    <td className="plr-amount">
                      {item.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
                {(!data.expenseSummary?.categories || data.expenseSummary.categories.length === 0) && (
                  <tr>
                    <td colSpan="2" className="plr-no-data-row">No expense data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="plr-totals">
            <div className="plr-total-item">
              <span>Total Income:</span>
              <span className="plr-total-income">
                ₹{(data.incomeSummary?.total || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="plr-total-item">
              <span>Total Expenses:</span>
              <span className="plr-total-expense">
                ₹{(data.expenseSummary?.total || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="plr-total-item plr-net-total">
              <span>Net {data.isProfit ? "Profit" : "Loss"}:</span>
              <span className={data.isProfit ? 'plr-net-profit' : 'plr-net-loss'}>
                ₹{Math.abs(data.netProfitLoss || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="plr-footer">
            <p>Generated on: {new Date().toLocaleDateString('en-IN', { 
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
          </div>
        </div>
      )}

      {/* No Data State */}
      {!loading && !data && filters.organisationId && filters.companyCode && (
        <div className="plr-no-data-message">
          <p>No data available for the selected filters. Please adjust your filters and try again.</p>
        </div>
      )}

      {/* Initial State - Prompt to select filters */}
      {!loading && !data && (!filters.organisationId || !filters.companyCode) && (
        <div className="plr-initial-state">
          <div className="plr-initial-icon">
            <i className="fas fa-filter"></i>
          </div>
          <h3>Select Filters to Generate Report</h3>
          <p>Please select an organisation and company code to view the Profit & Loss Statement.</p>
          <div className="plr-required-fields">
            <p><i className="fas fa-asterisk"></i> Organisation and Company Code are required fields</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitLossReport;